import * as z from 'zod';

import { LosId } from '../../BrandedIds';
import { add } from '../../utils/math';
import { DateString, MonetaryValue } from '../fields';

const HISTORICAL_DISBURSEMENT_BUCKET_KEYS = [
  'escrow',
  'suspense',
  'reserve',
  'holdback',
  'lossDraft',
  'other',
] as const;

export const HistoricalDisbursementRow = z
  .object({
    loanId: LosId,
    // Blank cells normalize to undefined so they store as NULL instead of ""
    // colliding in the (loan_id, transaction_id) unique index.
    transactionId: z
      .string()
      .transform((value) => value.trim() || undefined)
      .optional(),
    disbursementDate: DateString,
    disbursementType: z.string().optional(),
    disbursementRecipient: z.string().optional(),
    method: z.string().optional(),
    description: z.string().optional(),
    total: MonetaryValue,
    escrow: MonetaryValue.optional(),
    suspense: MonetaryValue.optional(),
    reserve: MonetaryValue.optional(),
    holdback: MonetaryValue.optional(),
    lossDraft: MonetaryValue.optional(),
    other: MonetaryValue.optional(),
  })
  .superRefine((data, ctx) => {
    const hasNonZeroBucket = HISTORICAL_DISBURSEMENT_BUCKET_KEYS.some((key) => data[key]);
    if (!hasNonZeroBucket) return;

    const bucketSum = HISTORICAL_DISBURSEMENT_BUCKET_KEYS.reduce((sum, key) => add(sum, data[key] ?? 0), 0);
    const total = add(data.total);
    if (bucketSum !== total) {
      ctx.addIssue({
        code: 'custom',
        message: `Total (${total}) must equal the sum of the accounting bucket columns (${bucketSum})`,
        path: ['total'],
      });
    }
  });

export type HistoricalDisbursementRow = z.infer<typeof HistoricalDisbursementRow>;

export const HISTORICAL_DISBURSEMENT_ROW_REQUIRED_FIELDS = Object.keys(HistoricalDisbursementRow.shape).filter(
  (key) => !HistoricalDisbursementRow.shape[key as keyof typeof HistoricalDisbursementRow.shape].isOptional(),
);
